import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { COMPANY_SIGNUP_URL } from '@/lib/companyAuth';

/**
 * Registration moved to Company after the identity merge.
 *
 * This page is now a redirect launcher: it bounces users to
 * `${VITE_COMPANY_SIGNUP_URL}` with a `return` param. After Company finishes
 * signup, it redirects back to /post-signup which calls the silent refresh and
 * lands the user on /feed (or surfaces the claim-username modal if no
 * EarthSocial username is set yet).
 */
export default function Register() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { theme } = useTheme();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useEffect(() => {
    if (!loading && user) navigate('/feed');
  }, [user, loading, navigate]);

  const returnTo = `${window.location.origin}/post-signup`;
  const signupUrl = `${COMPANY_SIGNUP_URL}?return=${encodeURIComponent(returnTo)}`;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center"
      style={{
        background: theme.appBg || 'linear-gradient(135deg, #006064, #00838f, #0097a7)',
        padding: '24px 16px',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        className="backdrop-blur-xl rounded-2xl shadow-2xl"
        style={{
          backgroundColor: theme.cardBg,
          border: `2px solid ${theme.accentColor}40`,
          maxWidth: '480px',
          width: '100%',
          padding: isMobile ? '32px 24px' : '48px',
          textAlign: 'center',
        }}
      >
        <h1
          className="font-bold mb-3"
          style={{ fontSize: isMobile ? '28px' : '34px', color: theme.textColor }}
        >
          Create your{' '}
          <span style={{ color: '#E91E63' }}>Earth Servers</span> account
        </h1>
        <p
          style={{
            color: theme.textColor,
            opacity: 0.8,
            fontSize: '15px',
            marginBottom: '32px',
            lineHeight: 1.5,
          }}
        >
          One account works across EarthSocial, the cosmetics market, the Minecraft mod, and
          future Earth Servers tools. Signup happens on Company — you'll come back here
          automatically once it's done.
        </p>
        <motion.a
          href={signupUrl}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full inline-flex items-center justify-center gap-2 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
          style={{
            padding: '14px 1rem',
            backgroundColor: theme.accentColor,
            color: '#1a1a1a',
            minHeight: '52px',
          }}
        >
          Sign up on Company
          <ExternalLink className="w-5 h-5" />
          <ArrowRight className="w-5 h-5" />
        </motion.a>

        <Link
          to="/login"
          className="block mt-6 transition-colors"
          style={{ color: theme.textColor, opacity: 0.8, fontSize: '14px' }}
        >
          Already have an account? <span className="underline font-semibold">Sign in</span>
        </Link>
      </motion.div>
    </div>
  );
}
